import { PostDimensions } from './postDimensions'

// Types we assign to the general container post itself, not the individual display
const postTypes = {
  BASE: 'BASE',
  REPOST: 'REPOST',
  REPLY_TOP_BOTTOM: 'REPLY_TOP_BOTTOM',
  REPLY_BOTTOM_ONLY: 'REPLY_BOTTOM_ONLY',
}

// Types we assign to the display of the post to get their dimensions and render correctly
const displayTypes = {
  base: 'base_display',
  repost: 'repost_display',
  replyTop: 'reply_top_display',
  replyBottom: 'reply_bottom_display',
}

const displayAccessorMap = {
  base_display: 'base',
  repost_display: 'base',
  reply_top_display: 'replyTop',
  reply_bottom_display: 'replyBottom',
}

const displayInterface = {
  data: {
    base: {
      post: null,
      user: null,
    },
    replyTop: {
      post: null,
      user: null,
    },
    replyBottom: {
      post: null,
      user: null,
    },
  },
  postType: null,
  totalHeightPx: null,
}

class PostDisplayParser {
  
  constructor({ post, displayInterface }) {
    this.display = structuredClone(displayInterface)
    this.post = post
    this.addData()
  }

  static insertDisplay({ post }) {
    const parsed = new this({ post, displayInterface })
    post.display = parsed.display
  }

  static insertDisplayAndDimensions({ post }) {
    if (!post) return;
    let base, replyTop, replyBottom

    // Insert the display object
    this.insertDisplay({ post })

    // Calculate the post heights
    switch (post?.display?.postType) {
      case postTypes.BASE:
        base = post.display.data.base.post;
        if (base) PostDimensions.insertDimensions({ post: base });
        post.display.totalHeightPx = base.dimensions.postTotal.heightInPx
        break;

      case postTypes.REPOST:
        base = post.display.data.base.post;
        if (base) PostDimensions.insertDimensions({ post: base });
        post.display.totalHeightPx = base.dimensions.postTotal.heightInPx
        break;

      case postTypes.REPLY_TOP_BOTTOM:
        replyTop = post.display.data.replyTop.post
        replyBottom = post.display.data.replyBottom.post

        if (replyTop) PostDimensions.insertDimensions({ post: replyTop })
        if (replyBottom) PostDimensions.insertDimensions({ post: replyBottom });

        post.display.totalHeightPx = replyTop.dimensions.postTotal.heightInPx + replyBottom.dimensions.postTotal.heightInPx
        break;

      case postTypes.REPLY_BOTTOM_ONLY:
        replyTop = post.display.data.replyTop.post
        replyBottom = post.display.data.replyBottom.post
        
        if (replyTop) PostDimensions.insertDimensions({ post: replyTop })
        if (replyBottom) PostDimensions.insertDimensions({ post: replyBottom })
        
        post.display.totalHeightPx = replyBottom.dimensions.postTotal.heightInPx
        break;
    }
  }

  static insertDisplayAndDimensionsAll(posts=[]) {
    if (Array.isArray(posts)) {
      posts.forEach((post) => this.insertDisplayAndDimensions({ post }))
    }
  }

  static displayAccessor(role) {
    return displayAccessorMap[role]
  }

  addData() {
    const { BASE, REPOST, REPLY_TOP_BOTTOM, REPLY_BOTTOM_ONLY } = postTypes
    const { display, post } = this

    if (post.isRepost) {
      display.postType = REPOST
      // NOTE: In the future, we may handle this by displaying a "no post found" message, but we don't have the time to address it now
      if (!post.original) {
        // TODO: We should make an error module to try and better handle custom errors, or use an error library with some of the work done for us.
        const message = `REPOST_WITH_NULL_ORIGINAL: Post ${post?.uuid} is a repost where the original post has been deleted`
        const newError = new Error(message)
        newError.context = { errorType: 'REPOST_WITH_NULL_ORIGINAL', message }
        throw newError
      }
      // Reposts are set from original post
      display.data.base.post = structuredClone(post.original)
      display.data.base.post.displayType = displayTypes.repost
      display.data.base.post.mainPostId = post.uuid
      display.data.base.user = post.originalPoster

    } else if (post.isReply) {
      // If it's a reply, throw error if no reply top
      // NOTE: In the future, we may handle this by displaying a "no post found" message, but we don't have the time to address it now
      if (!post.parent) {
        // TODO: We should make an error module to try and better handle custom errors, or use an error library with some of the work done for us.
        const message = `REPLY_WITH_NULL_PARENT: Post ${post?.uuid} is a reply post with null parent to display as reply top`
        const newError = new Error(message)
        newError.context = { errorType: 'REPLY_WITH_NULL_PARENT', message, }
        throw newError
      }

      // Determine the display. we set isReplyBottomOnly true when we want to see just the reply, not the parent (post show replies, for example)
      if (post.isReplyBottomOnly) {
        display.postType = REPLY_BOTTOM_ONLY
      } else {
        display.postType = REPLY_TOP_BOTTOM
      }

      // Top Post is set from parent post
      display.data.replyTop.post = structuredClone(post.parent)
      display.data.replyTop.post.displayType = displayTypes.replyTop
      display.data.replyTop.post.mainPostId = post.uuid
      display.data.replyTop.user = post.parentPoster
      // Bottom Post is set from base post 
      display.data.replyBottom.post = structuredClone(post)
      display.data.replyBottom.post.displayType = displayTypes.replyBottom
      display.data.replyBottom.post.mainPostId = post.uuid
      display.data.replyBottom.user = post.user

    } else {
      display.postType = BASE
      // Normal posts are set from base post
      display.data.base.post = structuredClone(post)
      display.data.base.post.displayType = displayTypes.base
      display.data.base.post.mainPostId = post.uuid
      display.data.base.user = post.user
    }
  }
}

export { PostDisplayParser, postTypes }
